import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Drawer, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { useDispatch } from 'react-redux';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { getOffsiteCampaignDetails } from '../../../redux/buzzbridge/actions';
import { campaignSeatIncreasePreviewUrl, campaignSeatIncreaseUrl } from '../../../configurations/api/url';
import { api } from '../../../configurations/api';
import { httpRequest } from '../../../redux/types';
import { formatCurrency } from '../../../constants/currency';
import { showToastMessage } from '../../../utils/AppUtils';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

export interface ISeatIncreasePreview {
    campaign_id: string;
    current_seats: number;
    filled_seats: number;
    additional_seats: number;
    new_seats: number;
    per_seat_cents: number;
    per_seat_usd: number;
    escrow_cents?: number;
    escrow_usd?: number;
    platform_fee_cents?: number;
    platform_fee_usd?: number;
    required_cents: number;
    required_usd: number;
    wallet_balance_cents: number;
    wallet_balance_usd: number;
    shortfall_cents: number;
    shortfall_usd: number;
    can_afford: boolean;
    new_total_budget_usd: number;
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
    canUseWallet?: boolean;
    onFundWallet: () => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

function StatRow({ label, value }: { label: string; value: string }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD, textAlign: 'right' }}>
                {value}
            </Typography>
        </Box>
    );
}

export function CampaignAddCreatorsPanel({ open, onClose, campaign, canUseWallet = true, onFundWallet }: Props) {
    const dispatch = useDispatch();
    const [seatsInput, setSeatsInput] = useState('1');
    const [preview, setPreview] = useState<ISeatIncreasePreview | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const additionalSeats = Math.max(0, parseInt(seatsInput, 10) || 0);
    const isSupported =
        Boolean(campaign.funded) &&
        !campaign.funding_paused &&
        campaign.campaign_type !== 'commission' &&
        !(campaign as { app_install?: { enabled?: boolean } }).app_install?.enabled &&
        campaign.status !== 'closed';

    const loadPreview = useCallback(async () => {
        if (!open || !isSupported || additionalSeats < 1) {
            setPreview(null);
            return;
        }
        setLoadingPreview(true);
        setError(null);
        try {
            const res = await api(campaignSeatIncreasePreviewUrl(campaign._id, additionalSeats), httpRequest.GET, null);
            setPreview((res as { data?: ISeatIncreasePreview })?.data ?? null);
        } catch (err: any) {
            setPreview(null);
            setError(
                err?.data?.message ||
                    err?.response?.data?.message ||
                    err?.message ||
                    'Could not load seat increase preview',
            );
        } finally {
            setLoadingPreview(false);
        }
    }, [open, isSupported, additionalSeats, campaign._id]);

    useEffect(() => {
        if (!open) return;
        setSeatsInput('1');
        setPreview(null);
        setError(null);
    }, [open, campaign._id]);

    useEffect(() => {
        if (!open) return;
        const t = window.setTimeout(() => {
            void loadPreview();
        }, 250);
        return () => window.clearTimeout(t);
    }, [open, loadPreview]);

    const handleConfirm = async () => {
        if (!preview?.can_afford || additionalSeats < 1 || confirming) return;
        setConfirming(true);
        setError(null);
        try {
            await api(campaignSeatIncreaseUrl(campaign._id), httpRequest.POST, {
                additional_seats: additionalSeats,
            });
            showToastMessage(
                `Added ${additionalSeats} creator seat${additionalSeats === 1 ? '' : 's'}. Confirmation emailed.`,
                'success',
            );
            dispatch(getOffsiteCampaignDetails({ campaignId: campaign._id, asBrandHost: true }));
            onClose();
        } catch (err: any) {
            const msg =
                err?.data?.message || err?.response?.data?.message || err?.message || 'Failed to add creator seats';
            setError(msg);
            // Insufficient wallet: keep panel open; Fund wallet CTA shows from preview.
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 480 },
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    backgroundImage: 'none',
                    p: 0,
                },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <PersonAddAlt1OutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 22 }} />
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}>
                        Add creators
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ px: 3, pb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {!isSupported ? (
                    <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' }}>
                        {!campaign.funded
                            ? 'Fund this campaign first, then you can add more creator seats.'
                            : campaign.funding_paused
                            ? 'Resume campaign funding before adding creator seats.'
                            : 'Adding seats is available for standard funded campaigns (not commission or app-install yet).'}
                    </Alert>
                ) : (
                    <>
                        <Box>
                            <FieldLabel>Extra creator seats</FieldLabel>
                            <TextField
                                type="number"
                                fullWidth
                                value={seatsInput}
                                onChange={e => setSeatsInput(e.target.value)}
                                inputProps={{ min: 1, max: 500 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                    },
                                }}
                            />
                            <Typography sx={{ mt: 1, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                                We lock the same pay-per-creator into escrow so current creators are not diluted.
                            </Typography>
                        </Box>

                        {loadingPreview ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: BRAND_LUXE_PRIMARY }} />
                            </Box>
                        ) : preview ? (
                            <Box
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    borderRadius: 2,
                                    p: 2,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <StatRow
                                    label="Current seats"
                                    value={`${preview.filled_seats} / ${preview.current_seats}`}
                                />
                                <StatRow label="After increase" value={String(preview.new_seats)} />
                                <StatRow label="Cost per creator" value={formatCurrency(preview.per_seat_usd)} />
                                <StatRow
                                    label="Escrow to lock"
                                    value={formatCurrency(preview.escrow_usd ?? preview.required_usd)}
                                />
                                <StatRow
                                    label="Seat increase fee"
                                    value={formatCurrency(preview.platform_fee_usd ?? 0)}
                                />
                                <StatRow label="Total from wallet" value={formatCurrency(preview.required_usd)} />
                                <StatRow label="Wallet balance" value={formatCurrency(preview.wallet_balance_usd)} />
                                {!preview.can_afford && (
                                    <StatRow label="Shortfall" value={formatCurrency(preview.shortfall_usd)} />
                                )}
                            </Box>
                        ) : null}

                        {error && (
                            <Alert severity="error" sx={{ bgcolor: 'rgba(255,80,80,0.08)' }}>
                                {error}
                            </Alert>
                        )}

                        {preview && !preview.can_afford && (
                            <Alert severity="warning" sx={{ bgcolor: 'rgba(255,201,101,0.08)', color: '#fff' }}>
                                Not enough wallet balance for escrow plus the seat-increase fee. Fund your wallet first,
                                then come back to add seats.
                            </Alert>
                        )}

                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                            A confirmation email is sent to the brand when seats are added.
                        </Typography>

                        {preview && !preview.can_afford ? (
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={!canUseWallet}
                                onClick={onFundWallet}
                                sx={{
                                    bgcolor: BRAND_LUXE_PRIMARY,
                                    color: '#1a1400',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    py: 1.4,
                                    borderRadius: 999,
                                    '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                                }}>
                                Fund wallet first
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={
                                    !preview?.can_afford ||
                                    additionalSeats < 1 ||
                                    confirming ||
                                    loadingPreview ||
                                    !canUseWallet
                                }
                                onClick={() => void handleConfirm()}
                                sx={{
                                    bgcolor: BRAND_LUXE_PRIMARY,
                                    color: '#1a1400',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    py: 1.4,
                                    borderRadius: 999,
                                    '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                                    '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.25)', color: 'rgba(0,0,0,0.4)' },
                                }}>
                                {confirming
                                    ? 'Locking escrow…'
                                    : `Add ${additionalSeats || ''} seat${additionalSeats === 1 ? '' : 's'}`}
                            </Button>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
}
